import { apiGet, apiPostPut, apiDelete } from "./api_methods";
import { RECRUITER_VERIFICATION_BASE } from "./endpoints";

/**
 * Create a new verification document (POST /v1/recruiter/verification).
 * @param {Object} body - Verification data:
 *   - applicationId: string (required) - MongoDB ObjectId
 *   - userId: string (required) - MongoDB ObjectId
 *   - overallStatus: number (optional) - 0=Pending, 1=Verified, 2=Unverified, 3=Rejected, 4=Under Review
 *   - verifications: array (optional) - Array of verification types
 *   - globalNotes: array (optional)
 * @returns {Promise<{ body, status }>}
 */
export async function createVerification(body) {
  return apiPostPut(body, RECRUITER_VERIFICATION_BASE, "POST");
}

/**
 * Get all verification documents with pagination and filters (GET /v1/recruiter/verification).
 * @param {Object} params - Query parameters:
 *   - page: number (optional) - Page number (default: 1)
 *   - limit: number (optional) - Items per page (default: 10)
 *   - status: number (optional) - Filter by overall status (0-4)
 *   - verificationType: string (optional) - Filter by verification type
 *   - applicationId: string (optional) - Filter by application ID
 *   - userId: string (optional) - Filter by user ID
 *   - sortBy: string (optional) - Sort field (default: createdAt)
 *   - sortOrder: string (optional) - Sort order: asc or desc (default: desc)
 * @returns {Promise<{ body, status }>}
 */
export async function getAllVerifications(params = {}) {
  const q = {};
  if (params.page != null) q.page = params.page;
  if (params.limit != null) q.limit = params.limit;
  if (params.status != null) q.status = params.status;
  if (params.verificationType != null && params.verificationType !== "") {
    q.verificationType = params.verificationType;
  }
  if (params.applicationId != null && params.applicationId !== "") {
    q.applicationId = params.applicationId;
  }
  if (params.userId != null && params.userId !== "") q.userId = params.userId;
  if (params.sortBy != null && params.sortBy !== "") q.sortBy = params.sortBy;
  if (params.sortOrder != null && params.sortOrder !== "") {
    q.sortOrder = params.sortOrder;
  }

  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${RECRUITER_VERIFICATION_BASE}?${query}`
    : RECRUITER_VERIFICATION_BASE;
  return apiGet(path);
}

/**
 * Get a specific verification document by ID (GET /v1/recruiter/verification/:id).
 * @param {string} verificationId - Verification document ID
 * @returns {Promise<{ body, status }>}
 */
export async function getVerificationById(verificationId) {
  return apiGet(`${RECRUITER_VERIFICATION_BASE}/${verificationId}`);
}

/**
 * Get verification document by application ID and user ID
 * (GET /v1/recruiter/verification/by-application/:applicationId/:userId).
 * @param {string} applicationId - Application ID
 * @param {string} userId - User ID
 * @returns {Promise<{ body, status }>}
 */
export async function getVerificationByApplicationAndUser(
  applicationId,
  userId,
) {
  return apiGet(
    `${RECRUITER_VERIFICATION_BASE}/by-application/${applicationId}/${userId}`,
  );
}

/**
 * Get aggregated verification statistics (GET /v1/recruiter/verification/stats/overview).
 * @returns {Promise<{ body, status }>}
 */
export async function getVerificationStats() {
  return apiGet(`${RECRUITER_VERIFICATION_BASE}/stats/overview`);
}

/**
 * Get summary of a specific verification document (GET /v1/recruiter/verification/:id/summary).
 * @param {string} verificationId - Verification document ID
 * @returns {Promise<{ body, status }>}
 */
export async function getVerificationSummary(verificationId) {
  return apiGet(`${RECRUITER_VERIFICATION_BASE}/${verificationId}/summary`);
}

/**
 * Update a verification document (PUT /v1/recruiter/verification/:id).
 * @param {string} verificationId - Verification document ID
 * @param {Object} body - Fields to update:
 *   - overallStatus: number (optional)
 *   - verifications: array (optional)
 *   - globalNotes: array (optional)
 * @returns {Promise<{ body, status }>}
 */
export async function updateVerification(verificationId, body) {
  return apiPostPut(
    body,
    `${RECRUITER_VERIFICATION_BASE}/${verificationId}`,
    "PUT",
  );
}

/**
 * Add a new verification type to an existing document
 * (POST /v1/recruiter/verification/:id/verifications).
 * @param {string} verificationId - Verification document ID
 * @param {Object} body - Verification type data:
 *   - verificationType: string (required)
 *   - name: string (required)
 *   - description: string (required)
 *   - verifiedBy: object (required) - { name, email?, phone?, role?, description? }
 *   - status: number (optional) - 0-4
 *   - priority: string (optional) - low|medium|high|urgent
 * @returns {Promise<{ body, status }>}
 */
export async function addVerificationType(verificationId, body) {
  return apiPostPut(
    body,
    `${RECRUITER_VERIFICATION_BASE}/${verificationId}/verifications`,
    "POST",
  );
}

/**
 * Update the status of a specific verification type
 * (PUT /v1/recruiter/verification/:id/status).
 * @param {string} verificationId - Verification document ID
 * @param {Object} body - Status update data:
 *   - verificationType: string (required)
 *   - status: number (required) - 0-4
 *   - notes: string (optional)
 *   - rejectionReason: string (optional - required if status=3)
 *   - rejectedBy: string (optional) - MongoDB ObjectId
 * @returns {Promise<{ body, status }>}
 */
export async function updateVerificationStatus(verificationId, body) {
  return apiPostPut(
    body,
    `${RECRUITER_VERIFICATION_BASE}/${verificationId}/status`,
    "PUT",
  );
}

/**
 * Add proof documents to a specific verification type
 * (POST /v1/recruiter/verification/:id/proof).
 * @param {string} verificationId - Verification document ID
 * @param {Object} body - Proof data:
 *   - verificationType: string (required)
 *   - proof: object | array - Single proof object or array of proof objects
 *     - type: string (required) - image|video|audio|document|pdf|docx|link|etc.
 *     - name: string (required)
 *     - description: string (optional)
 *     - url: string (optional)
 *     - additionalInfo: string (optional)
 * @returns {Promise<{ body, status }>}
 */
export async function addProofDocuments(verificationId, body) {
  return apiPostPut(
    body,
    `${RECRUITER_VERIFICATION_BASE}/${verificationId}/proof`,
    "POST",
  );
}

/**
 * Add a global note to the verification document
 * (POST /v1/recruiter/verification/:id/notes).
 * @param {string} verificationId - Verification document ID
 * @param {Object} body - Note data:
 *   - note: string (required)
 *   - addedBy: string (required) - MongoDB ObjectId
 * @returns {Promise<{ body, status }>}
 */
export async function addGlobalNote(verificationId, body) {
  return apiPostPut(
    body,
    `${RECRUITER_VERIFICATION_BASE}/${verificationId}/notes`,
    "POST",
  );
}

/**
 * Bulk update multiple verification statuses (PUT /v1/recruiter/verification/bulk-update).
 * @param {Object} body - Bulk update data:
 *   - updates: array (required) - Array of update objects:
 *     - id: string (required) - Verification document ID
 *     - verificationType: string (required)
 *     - status: number (required) - 0-4
 *     - notes: string (optional)
 *     - rejectionReason: string (optional)
 *     - rejectedBy: string (optional)
 * @returns {Promise<{ body, status }>}
 */
export async function bulkUpdateVerificationStatus(body) {
  return apiPostPut(body, `${RECRUITER_VERIFICATION_BASE}/bulk-update`, "PUT");
}

/**
 * Remove a specific verification type from a document
 * (DELETE /v1/recruiter/verification/:id/verifications/:verificationType).
 * @param {string} verificationId - Verification document ID
 * @param {string} verificationType - Type to remove
 * @returns {Promise<{ body, status }>}
 */
export async function removeVerificationType(verificationId, verificationType) {
  return apiDelete(
    `${RECRUITER_VERIFICATION_BASE}/${verificationId}/verifications/${verificationType}`,
  );
}

/**
 * Soft delete a verification document (DELETE /v1/recruiter/verification/:id).
 * @param {string} verificationId - Verification document ID
 * @returns {Promise<{ body, status }>}
 */
export async function deleteVerification(verificationId) {
  return apiDelete(`${RECRUITER_VERIFICATION_BASE}/${verificationId}`);
}

/**
 * Verification Status Constants
 */
export const VERIFICATION_STATUS = {
  PENDING: 0,
  VERIFIED: 1,
  UNVERIFIED: 2,
  REJECTED: 3,
  UNDER_REVIEW: 4,
};

/**
 * Verification Status Labels
 */
export const VERIFICATION_STATUS_LABELS = {
  0: "Pending",
  1: "Verified",
  2: "Unverified",
  3: "Rejected",
  4: "Under Review",
};

/**
 * Verification Types
 */
export const VERIFICATION_TYPES = {
  EDUCATION: "education",
  EXPERIENCE: "experience",
  CRIMINAL_CHECK: "criminal_check",
  IDENTITY: "identity",
  ADDRESS: "address",
  EMPLOYMENT: "employment",
  REFERENCE: "reference",
  CERTIFICATION: "certification",
  LICENSE: "license",
  OTHER: "other",
};

/**
 * Verification Priority Levels
 */
export const VERIFICATION_PRIORITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  URGENT: "urgent",
};

/**
 * Proof Document Types
 */
export const PROOF_TYPES = {
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
  DOCUMENT: "document",
  PDF: "pdf",
  DOCX: "docx",
  LINK: "link",
  CSV: "csv",
  XLSX: "xlsx",
  PPTX: "pptx",
  ZIP: "zip",
  TEXT: "text",
  OTHER: "other",
};
